import React, { useEffect, useState } from "react";
import { Trophy, X, Medal, ShieldCheck, Crown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { modeColor } from "@/lib/gamemode";
import { SupporterBadge } from "@/components/SupporterBadge";

export default function Leaderboard() {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("all");
  const [data, setData] = useState({ entries: [], season: null });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (!open) return;
    setPage(0);
    setLoading(true);
    api
      .get(`/leaderboard?scope=${scope}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, scope]);

  const medal = (rank) => {
    if (rank === 1) return "#FFD700";
    if (rank === 2) return "#C0C0C0";
    if (rank === 3) return "#CD7F32";
    return null;
  };

  const entries = data.entries || [];

  return (
    <>
      <button data-testid="open-leaderboard" onClick={() => setOpen(true)} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2">
        <Trophy size={16} /> <span>Leaderboard</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-6 w-full max-w-lg max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="leaderboard-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><Trophy size={22} /> Hall of Fame</h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-leaderboard"><X size={16} /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <button data-testid="lb-scope-all" onClick={() => setScope("all")} className={`osrs-btn py-2 text-xs ${scope === "all" ? "" : "opacity-60"}`}>
                All-Time
              </button>
              <button data-testid="lb-scope-season" onClick={() => setScope("season")} className={`osrs-btn py-2 text-xs ${scope === "season" ? "" : "opacity-60"}`}>
                This Month{data.season && scope === "season" ? ` (${data.season})` : ""}
              </button>
            </div>

            <p className="osrs-shadow text-[#E6A519] text-xs mb-4">
              {scope === "season" ? "This month's race — resets on the 1st. Ranked by net points earned this month." : "Ranked by total net points (completions minus skips)."}
            </p>

            {loading ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8">Loading rankings...</p>
            ) : entries.length === 0 ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8" data-testid="leaderboard-empty">
                {scope === "season" ? "No points earned yet this month. Claim the top spot!" : "No points earned yet. Be the first to complete a task!"}
              </p>
            ) : (
              <div className="flex flex-col gap-1" data-testid="leaderboard-list">
                {entries.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE).map((r) => (
                  <div
                    key={`${r.rank}-${r.name}`}
                    data-testid={`leaderboard-row-${r.rank}`}
                    className={`flex items-center gap-3 px-3 py-2 border-2 border-black ${r.is_me ? "bg-[#2f4a1c]" : "bg-[#241b10]"}`}
                  >
                    <span className="osrs-num text-xl w-8 text-center" style={{ color: medal(r.rank) || "#E6A519" }}>
                      {medal(r.rank) ? <Medal size={20} className="inline" style={{ color: medal(r.rank) }} /> : r.rank}
                    </span>
                    <span className="flex-1 osrs-shadow text-[#FFCF3F] font-bold truncate">
                      {r.rsn ? (
                        <a
                          href={`/u/${encodeURIComponent(r.rsn)}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`leaderboard-link-${r.rank}`}
                          className="hover:underline decoration-dotted underline-offset-2"
                          title={`View ${r.rsn}'s rank card`}
                        >
                          {r.name}
                        </a>
                      ) : r.name}
                      {r.is_supporter && <SupporterBadge size={12} className="ml-1" />}
                      {r.is_legend && (
                        <Crown size={13} className="inline ml-1 text-[#FFD700]" style={{ filter: "drop-shadow(1px 1px 0 #000)" }}
                          data-testid={`legend-flair-${r.rank}`} title="Legend — 200+ tasks completed" />
                      )}
                      {r.gamemode && r.gamemode !== "main" && (
                        <span className="osrs-num text-[10px] ml-1 uppercase" style={{ color: modeColor(r.gamemode) }}>
                          {r.gamemode === "hardcore" ? "HCIM" : r.gamemode === "ultimate" ? "UIM" : r.gamemode === "ironman" ? "IM" : ""}
                        </span>
                      )}
                      {r.is_me && <span className="text-[#00FF00] text-xs ml-1">(you)</span>}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#00FF00] text-xs osrs-num" title="Verified completions">
                      <ShieldCheck size={12} /> {r.verified}
                    </span>
                    <span className="osrs-num text-[#FFCF3F] osrs-shadow text-lg w-20 text-right">{r.total_points} pts</span>
                  </div>
                ))}
              </div>
            )}

            {!loading && entries.length > PAGE_SIZE && (() => {
              const pageCount = Math.ceil(entries.length / PAGE_SIZE);
              return (
                <div className="flex items-center justify-between gap-2 mt-4" data-testid="leaderboard-pagination">
                  <button
                    data-testid="lb-prev-page"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="osrs-btn px-3 py-1.5 text-xs disabled:opacity-40"
                  >
                    ‹ Prev
                  </button>
                  <span className="osrs-shadow text-[11px] text-[#b9a06a]" data-testid="lb-page-indicator">
                    Page <span className="osrs-num text-[#FFCF3F]">{page + 1}</span> of <span className="osrs-num text-[#FFCF3F]">{pageCount}</span>
                    <span className="text-[#8a7a54]"> · ranks {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, entries.length)}</span>
                  </span>
                  <button
                    data-testid="lb-next-page"
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={page >= pageCount - 1}
                    className="osrs-btn px-3 py-1.5 text-xs disabled:opacity-40"
                  >
                    Next ›
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
